import { faChevronDown } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import KARTELogo from '../../../images/site-wide/karte-logo_horizontal.inline.svg'
import * as SiteWideStyles from '../../../styles/abstract/SiteWide.css'
import * as HeaderDesktopStyles from '../../../styles/foundation/HeaderDesktop.css'
import { formattedMessageValues } from '../../../utils/FormattedMessageValues'
import UiLink from '../../ui/v1/Link'
import { HeaderNavigationSubMenuEbook } from './ui/HeaderNavigationSubMenuEbook'
import { HeaderNavigationSubMenuExample } from './ui/HeaderNavigationSubMenuExample'
import { HeaderNavigationSubMenuProduct } from './ui/HeaderNavigationSubMenuProduct'
import { HeaderNavigationSubMenuResource } from './ui/HeaderNavigationSubMenuResource'
import { HeaderNavigationSubMenuSeminar } from './ui/HeaderNavigationSubMenuSeminar'

type HeaderDesktopProps = {
  globalNavigationEntity: Queries.Hygraph_GlobalNavigation
}

const HeaderDesktop = ({ globalNavigationEntity }: HeaderDesktopProps) => {
  const { messages } = useIntl()
  const [hovered, setHovered] = useState(0)
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null)

  const handleMouseEnter = (number: number) => {
    if (hovered === 0) {
      setHovered(number)
    } else {
      if (hoverTimeout.current) clearTimeout(hoverTimeout.current)

      hoverTimeout.current = setTimeout(() => {
        setHovered(number)
      }, 100)
    }
  }

  const handleMouseLeave = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current)

    setHovered(0)
  }

  return (
    <header className={classNames(HeaderDesktopStyles.Container, SiteWideStyles.DisplayDesktopOnly)}>
      <div className={classNames(HeaderDesktopStyles.Inner, SiteWideStyles.Inner)}>
        <UiLink
          to="/"
          rel="noopener"
          aria-label={
            messages && typeof messages['components.foundation.header.logo.label'] === 'string'
              ? messages['components.foundation.header.logo.label']
              : 'ホームへ'
          }
          className={HeaderDesktopStyles.Logo}
        >
          <KARTELogo width="135" height="24" />
        </UiLink>

        <nav
          aria-label={
            messages && typeof messages['components.foundation.header.nav.desktop.label'] === 'string'
              ? messages['components.foundation.header.nav.desktop.label']
              : 'デスクトップヘッダーナビゲーション'
          }
          className={HeaderDesktopStyles.Navigation}
        >
          <ul onMouseLeave={handleMouseLeave} className={HeaderDesktopStyles.Menu}>
            <li onMouseMove={() => handleMouseEnter(1)} className={`${hovered === 1 ? 'hover' : ''}`}>
              <div className={HeaderDesktopStyles.Menu_Category}>
                <FormattedMessage
                  id="components.foundation.header.nav.product.categoryName"
                  defaultMessage="プロダクト"
                  values={{ ...formattedMessageValues }}
                />
                <FontAwesomeIcon icon={faChevronDown} width="10" />
              </div>
              <div className={HeaderDesktopStyles.Menu_Content}>
                <HeaderNavigationSubMenuProduct
                  packageEntity={globalNavigationEntity.package}
                  featureEntity={globalNavigationEntity.feature}
                />
              </div>
            </li>

            <li onMouseMove={() => handleMouseEnter(2)} className={`${hovered === 2 ? 'hover' : ''}`}>
              <div className={HeaderDesktopStyles.Menu_Category}>
                <FormattedMessage
                  id="components.foundation.header.nav.example.categoryName"
                  defaultMessage="事例"
                  values={{ ...formattedMessageValues }}
                />
                <FontAwesomeIcon icon={faChevronDown} width="10" />
              </div>
              <div className={HeaderDesktopStyles.Menu_Content}>
                <HeaderNavigationSubMenuExample
                  industryEntity={globalNavigationEntity.industry}
                  interviewEntity={globalNavigationEntity.interview}
                  caseStudyEntity={globalNavigationEntity.caseStudy}
                />
              </div>
            </li>

            <li onMouseMove={() => handleMouseEnter(3)} className={`${hovered === 3 ? 'hover' : ''}`}>
              <div className={HeaderDesktopStyles.Menu_Category}>
                <FormattedMessage
                  id="components.foundation.header.nav.seminar.categoryName"
                  defaultMessage="セミナー"
                  values={{ ...formattedMessageValues }}
                />
                <FontAwesomeIcon icon={faChevronDown} width="10" />
              </div>
              <div className={HeaderDesktopStyles.Menu_Content}>
                <HeaderNavigationSubMenuSeminar seminarEntity={globalNavigationEntity.seminar} />
              </div>
            </li>

            <li onMouseMove={() => handleMouseEnter(4)} className={`${hovered === 4 ? 'hover' : ''}`}>
              <div className={HeaderDesktopStyles.Menu_Category}>
                <FormattedMessage
                  id="components.foundation.header.nav.documents.categoryName"
                  defaultMessage="資料一覧"
                  values={{ ...formattedMessageValues }}
                />
                <FontAwesomeIcon icon={faChevronDown} width="10" />
              </div>
              <div className={HeaderDesktopStyles.Menu_Content}>
                <HeaderNavigationSubMenuEbook ebookEntity={globalNavigationEntity.ebook} />
              </div>
            </li>

            <li onMouseMove={() => handleMouseEnter(5)} className={`${hovered === 5 ? 'hover' : ''}`}>
              <div className={HeaderDesktopStyles.Menu_Category}>
                <FormattedMessage
                  id="components.foundation.header.nav.resource.categoryName"
                  defaultMessage="リソース"
                  values={{ ...formattedMessageValues }}
                />
                <FontAwesomeIcon icon={faChevronDown} width="10" />
              </div>
              <div className={HeaderDesktopStyles.Menu_Content}>
                <HeaderNavigationSubMenuResource
                  supportEntity={globalNavigationEntity.support}
                  operationalSupportEntry={globalNavigationEntity.operationalSupport}
                  specEntity={globalNavigationEntity.spec}
                />
              </div>
            </li>
          </ul>

          <div className={HeaderDesktopStyles.Buttons}>
            <ul className={HeaderDesktopStyles.Buttons_Group}>
              <li className={HeaderDesktopStyles.Buttons_Item}>
                <UiLink
                  to="https://admin.karte.io/"
                  target="_blank"
                  rel="noopener"
                  aria-label={
                    messages && typeof messages['components.foundation.header.buttons.login'] === 'string'
                      ? messages['components.foundation.header.buttons.login']
                      : 'ログイン'
                  }
                  className={HeaderDesktopStyles.Buttons_Login}
                >
                  <FormattedMessage
                    id="components.foundation.header.buttons.login"
                    defaultMessage="ログイン"
                    values={{ ...formattedMessageValues }}
                  />
                </UiLink>
              </li>
              <li className={HeaderDesktopStyles.Buttons_Item}>
                <UiLink
                  to="/enterprise/ebook/"
                  target="_blank"
                  rel="noopener"
                  className={SiteWideStyles.Button({
                    color: 'primary',
                    mobileSize: 'extraSmall',
                    desktopSize: 'small',
                  })}
                >
                  <FormattedMessage
                    id="components.foundation.header.buttons.download"
                    defaultMessage="資料ダウンロード"
                    values={{ ...formattedMessageValues }}
                  />
                </UiLink>
              </li>
              <li className={HeaderDesktopStyles.Buttons_Item}>
                <UiLink
                  to="/enterprise/"
                  target="_blank"
                  rel="noopener"
                  className={SiteWideStyles.Button({
                    color: 'white',
                    mobileSize: 'extraSmall',
                    desktopSize: 'small',
                  })}
                >
                  <FormattedMessage
                    id="components.foundation.header.buttons.contact"
                    defaultMessage="お問い合わせ"
                    values={{ ...formattedMessageValues }}
                  />
                </UiLink>
              </li>
            </ul>
          </div>
        </nav>
      </div>
    </header>
  )
}

export default HeaderDesktop
